import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../amplify/data/resource';
import { getUrl } from 'aws-amplify/storage';

interface Course {
  id: string;
  courseId?: string;
  title: string;
  punchline?: string;
  shortDescription?: string;
  description: string;
  price: string;
  tutorId?: string;
  tutor?: { id: string; name: string };
  startDate?: string;
  endDate?: string;
  targetgroup?: string;
  location?: string;
  urlSlug?: string;
  partnerId?: string;
  partner?: {
    id: string;
    name: string;
    logoPath?: string;
  };
  partner2Id?: string;
  partner2?: {
    id: string;
    name: string;
    logoPath?: string;
  };
  isExternalRegistration?: boolean;
  externalRegistrationUrl?: string;
  isActive: boolean;
  discountInformation?: string;
  createdAt: string;
  updatedAt: string;
}

const CourseList: React.FC = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [partnerLogoUrls, setPartnerLogoUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchCourses();
  }, []);

  const handleCourseClick = (course: Course) => {
    if (course.isExternalRegistration && course.externalRegistrationUrl) {
      try {
        const url = course.externalRegistrationUrl;
        window.open(url, '_blank', 'noopener,noreferrer');
      } catch {
        // Fallback to same-tab if window.open is blocked
        window.location.href = course.externalRegistrationUrl;
      }
      return;
    }
    const slug = course.urlSlug || createSlugFromTitle(course.title);
    navigate(`/courses/${slug}`);
  };

  const createSlugFromTitle = (title: string): string => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const fetchCourses = async () => {
    try {
      setLoading(true);
      // #region agent log - Hypothesis A,C: Check localStorage state before fetch
      const fetchStartTime = performance.now();
      const storageState = {
        localStorageSize: localStorage.length,
        cognitoIdentityKeys: Object.keys(localStorage).filter(k => k.includes('CognitoIdentity')),
        amplifyKeys: Object.keys(localStorage).filter(k => k.includes('amplify')),
        pageLoadCount: parseInt(sessionStorage.getItem('courseListLoadCount') || '0') + 1
      };
      sessionStorage.setItem('courseListLoadCount', storageState.pageLoadCount.toString());
      console.log('🔍 [CHROME-DEBUG] CourseList fetch attempt #' + storageState.pageLoadCount, storageState);
      // #endregion
      
      console.log('Fetching courses from database...');
      
      // Add safety check and error handling for client generation
      let client;
      try {
        console.log('Generating GraphQL client...');
        
        // #region agent log - Hypothesis A: Check credential retrieval timing
        const credStartTime = performance.now();
        // #endregion
        
        // Get auth session to check IAM credentials
        const { fetchAuthSession } = await import('aws-amplify/auth');
        const session = await fetchAuthSession();
        
        // #region agent log - Hypothesis A,C: Detailed credential state
        const credEndTime = performance.now();
        const credentialDetails = {
          fetchDuration: (credEndTime - credStartTime).toFixed(2) + 'ms',
          hasCredentials: !!session.credentials,
          hasAccessKeyId: !!(session.credentials as any)?.accessKeyId,
          accessKeyIdPrefix: (session.credentials as any)?.accessKeyId?.substring(0, 10),
          hasSecretAccessKey: !!(session.credentials as any)?.secretAccessKey,
          hasSessionToken: !!(session.credentials as any)?.sessionToken,
          sessionTokenPrefix: (session.credentials as any)?.sessionToken?.substring(0, 20),
          identityId: session.identityId,
          identityIdFromStorage: localStorage.getItem('aws.cognito.identity-id.eu-west-1:c6265f50-e9a3-4b92-9b52-02ec41e15e5d'),
          expiration: session.credentials?.expiration?.toISOString(),
          isExpired: session.credentials?.expiration ? new Date(session.credentials.expiration) < new Date() : null
        };
        console.log('🔍 [CHROME-DEBUG] IAM credentials retrieved:', credentialDetails);
        // #endregion
        
        console.log('🔧 DEBUG: Auth session for IAM', {
          hasCredentials: !!session.credentials,
          hasAccessKeyId: !!(session.credentials as any)?.accessKeyId,
          hasSecretAccessKey: !!(session.credentials as any)?.secretAccessKey,
          hasSessionToken: !!(session.credentials as any)?.sessionToken,
          identityId: session.identityId,
          expiration: session.credentials?.expiration?.toISOString()
        });
        
        client = generateClient<Schema>({ authMode: 'iam' });
        console.log('✅ Client generated with authMode: iam');
      } catch (clientError) {
        // #region agent log - Hypothesis A,C,E: Log client generation failure
        console.error('🔍 [CHROME-DEBUG] Client generation FAILED:', {
          error: clientError instanceof Error ? clientError.message : String(clientError),
          errorName: clientError instanceof Error ? clientError.name : undefined,
          stack: clientError instanceof Error ? clientError.stack : undefined
        });
        // #endregion
        console.error('❌ Failed to generate client:', clientError);
        throw new Error(`Client generation failed: ${clientError instanceof Error ? clientError.message : 'Unknown error'}`);
      }
      
      // Fetch courses with partner relationship - try different approaches
      let data;
      
      console.log('🔍 Step 1: Attempting basic course fetch...');
      
      // #region agent log - Hypothesis E: Check if service worker is interfering
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(registrations => {
          console.log('🔍 [CHROME-DEBUG] Service workers active:', {
            count: registrations.length,
            workers: registrations.map(reg => ({ scope: reg.scope, state: reg.active?.state }))
          });
        });
      }
      // #endregion
      
      try {
        // #region agent log - Hypothesis A: Time the GraphQL request
        const queryStartTime = performance.now();
        // #endregion
        
        const basicResult = await client.models.Course.list();
        
        // #region agent log - Hypothesis A,E: Detailed response analysis
        const queryEndTime = performance.now();
        const responseAnalysis = {
          queryDuration: (queryEndTime - queryStartTime).toFixed(2) + 'ms',
          totalDuration: (queryEndTime - fetchStartTime).toFixed(2) + 'ms',
          dataCount: basicResult.data?.length || 0,
          hasErrors: !!(basicResult.errors && basicResult.errors.length > 0),
          errorCount: basicResult.errors?.length || 0,
          firstError: basicResult.errors?.[0] ? {
            message: (basicResult.errors[0] as any).message,
            errorType: (basicResult.errors[0] as any).errorType,
            errorInfo: (basicResult.errors[0] as any).errorInfo
          } : null,
          responseType: typeof basicResult,
          hasData: !!basicResult.data
        };
        console.log('🔍 [CHROME-DEBUG] GraphQL response analysis:', responseAnalysis);
        // #endregion
        
        console.log('🔧 DEBUG: Course.list() response', {
          dataCount: basicResult.data?.length || 0,
          hasErrors: !!(basicResult.errors && basicResult.errors.length > 0),
          errorCount: basicResult.errors?.length || 0,
          firstError: basicResult.errors?.[0] ? {
            message: (basicResult.errors[0] as any).message,
            errorType: (basicResult.errors[0] as any).errorType,
            errorInfo: (basicResult.errors[0] as any).errorInfo
          } : null
        });
        
        console.log('✅ Basic course fetch successful:', basicResult.data?.length || 0, 'courses');
        data = basicResult.data;
      } catch (basicError) {
        // #region agent log - Hypothesis A,C,E: Detailed error capture
        console.error('🔍 [CHROME-DEBUG] Course fetch exception:', {
          error: basicError instanceof Error ? basicError.message : String(basicError),
          errorName: basicError instanceof Error ? basicError.name : typeof basicError,
          stack: basicError instanceof Error ? basicError.stack?.split('\n').slice(0, 3) : undefined,
          storageAtError: {
            localStorageSize: localStorage.length,
            cognitoKeys: Object.keys(localStorage).filter(k => k.includes('CognitoIdentity'))
          }
        });
        // #endregion
        console.error('❌ Basic course fetch failed:', basicError);
        throw basicError;
      }
      
      console.log('🔍 Step 2: Attempting to fetch with partner data...');
      try {
        const result = await client.models.Course.list({
          selectionSet: [
            'id', 'courseId', 'title', 'punchline', 'shortDescription', 'description', 'price',
            'tutorId', 'startDate', 'endDate', 'targetgroup', 'location', 'urlSlug',
            'isExternalRegistration', 'externalRegistrationUrl',
            'partnerId', 'partner2Id', 'isActive', 'createdAt', 'updatedAt',
            'partner.*', 'tutor.*'
          ]
        });
        data = result.data;
        console.log('✅ Successfully fetched courses with partner data');
      } catch (partnerError) {
        console.warn('⚠️ Partner relationship query failed, using basic data:', partnerError);
        console.warn('Error details:', partnerError instanceof Error ? partnerError.message : String(partnerError));
        // Keep the basic data we already fetched
      }
      console.log('Courses fetched successfully:', data?.length || 0, 'courses');
      console.log('Raw course data with partners:', data);

      // Augment courses with partner objects if missing by building a partner map
      let partnerMap: Map<string, any> | null = null;
      try {
        const partnersResult = await client.models.Partner.list({
          selectionSet: ['id','name','logoPath']
        });
        const allPartners = partnersResult.data || [];
        partnerMap = new Map<string, any>(
          allPartners.map((p: any) => [p.id, p])
        );

        // Resolve S3 keys to URLs for partner logos
        const urlEntries: Array<[string, string]> = [];
        await Promise.all((allPartners as any[]).map(async (p) => {
          const key: string | undefined = p?.logoPath;
          if (!key) return;
          try {
            if (typeof key === 'string' && !key.startsWith('http') && !key.startsWith('/')) {
              // Use S3 URLs with local fallback
              try {
                const publicPath = key.startsWith('public/') ? key : `public/${key}`;
                const { url } = await getUrl({ path: publicPath });
                urlEntries.push([p.id, url.toString()]);
              } catch (s3Error) {
                console.warn(`S3 access failed for partner ${p.name}, using local fallback:`, s3Error);
                // Fallback to local assets if S3 fails
                const fallbackPath = key.replace('public/', '/assets/images/');
                urlEntries.push([p.id, fallbackPath]);
              }
            }
          } catch (e) {
            // ignore individual failures
          }
        }));
        if (urlEntries.length > 0) {
          setPartnerLogoUrls((prev) => ({ ...prev, ...Object.fromEntries(urlEntries) }));
        }
        if (Array.isArray(data) && partnerMap) {
          data = data.map((c: any) => {
            if (!c?.partner && c?.partnerId && partnerMap && partnerMap.has(c.partnerId)) {
              return { ...c, partner: partnerMap.get(c.partnerId) };
            }
            if (!c?.partner2 && c?.partner2Id && partnerMap && partnerMap.has(c.partner2Id)) {
              return { ...c, partner: c.partner, partner2: partnerMap.get(c.partner2Id) };
            }
            return c;
          });
        }
      } catch (partnerAugmentError) {
        console.warn('⚠️ Could not augment courses with partner objects:', partnerAugmentError);
      }
      
      // Debug URL slugs and partner data
      data?.forEach((course: any, index: number) => {
        console.log(`Course ${index + 1}: "${course.title}" → urlSlug: "${course.urlSlug || 'not-set'}", partnerId: "${course.partnerId || 'none'}", partner:`, course.partner);
      });
      
      // Filter out any null/undefined items and ensure all required properties exist
      const validCourses = (data || []).filter((course: any) => 
        course && 
        course.id && 
        course.title && 
        course.description !== null &&
        course.price !== null
      );
      
      console.log('Valid courses after filtering:', validCourses);
      setCourses(validCourses as Course[]);
      setError(null);
    } catch (err) {
      console.error('Error fetching courses:', err);
      setError(`Failed to load courses: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Pagination + search
  useEffect(() => {
    if (typeof window !== 'undefined' && (window as any).requestAnimationFrame) {
      (window as any).requestAnimationFrame(() => setCurrentPage(1));
    } else {
      setCurrentPage(1);
    }
  }, [searchQuery]);

  const pageSize = 6;
  const filteredCourses: Course[] = (courses || []).filter((course: Course) => {
    // Only show active courses
    if (!course.isActive) return false;
    
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const haystack = [
      course.title,
      course.description,
      course.location,
      course.price,
      course.targetgroup,
      course.courseId,
      course.urlSlug,
      course.partner?.name,
      course.partner2?.name
    ]
      .filter(Boolean)
      .join(' \n ')
      .toLowerCase();
    return haystack.includes(q);
  });
  const totalPages = Math.max(1, Math.ceil(filteredCourses.length / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedCourses = filteredCourses.slice(startIndex, startIndex + pageSize);
  const activeCount = (courses || []).filter((c) => c && c.isActive).length;


  if (loading) {
    return (
      <div className="loading" style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
        <div className="spinner"></div>
        <p style={{ textAlign: 'center', margin: '0 auto' }}>Kurse werden geladen...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error">
        <h3>Unable to load courses</h3>
        <p>{error}</p>
        <button onClick={fetchCourses} className="retry-btn">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .partner-item {
          align-items: flex-start !important;
        }
        .course-search-container { position:relative; width: 100%; display:flex; justify-content:center; margin:24px 0 8px 0; }
        .course-search-inner { width:100%; max-width:760px; display:flex; align-items:center; }
        .course-search-input { flex:1; padding:14px 16px; border:1px solid #d1d5db; border-radius:9999px; font-size:1rem; outline:none; transition: box-shadow .15s ease, border-color .15s ease; }
        .course-search-input:focus { border-color:#008F99; box-shadow:0 0 0 3px rgba(0,143,153,.15); }
        .pager-controls { display:flex; align-items:center; gap:6px; }
        .pager-btn { appearance:none; border:1px solid #008F99; background:#008F99; color:#fff; width:44px; height:44px; border-radius:50%; font-size:20px; line-height:1; display:flex; align-items:center; justify-content:center; cursor:pointer; box-shadow:0 2px 6px rgba(0,0,0,0.12); }
        .pager-btn:disabled { opacity:.4; cursor:not-allowed; }
        .pager-info { display:none; }
        .pager-edge { position:absolute; top:50%; transform:translateY(-50%); }
        .pager-edge--left { left:0; }
        .pager-edge--right { right:0; }
        .pager-bottom { display:flex; align-items:center; justify-content:center; gap:8px; margin-top:16px; }
        .pager-bottom-container { position:relative; width:100%; margin-top:16px; min-height:44px; }
        .pager-bottom-center { position:relative; width:100%; display:flex; align-items:center; justify-content:center; }
        
        .course-count-highlight { color:#008F99; font-weight:700; margin-top:0; text-align:center; }
        .course-header h1 { margin-bottom: 4px; }

        .partner-info {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          flex-wrap: wrap;
          width: 100%;
        }

        .partner-logos {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 16px;
          width: 100%;
          flex-wrap: wrap; /* allow wrapping to prevent overflow */
        }

        /* Stack label above logos and center them using full width */
        .info-item.partner-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }
        .info-item.partner-item .label {
          width: 100%;
          text-align: center;
          margin-bottom: 12px;
          color: #008F99;
          font-size: 1.5rem;
          font-weight: 600;
        }
        
        .partner-logo-container {
          display: flex;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
          width: 240px;
          height: 160px;
          flex: 0 1 240px;
          background: transparent;
          border-radius: 8px;
          padding: 0 !important;
          line-height: 0; /* remove inline-image baseline gap */
          border: none;
        }
        
        .partner-logo-small {
          display: block; /* remove inline spacing */
          margin: 0;
          padding: 0;
          width: 100%;
          height: 100%;
          object-fit: contain;
          outline: none;
        }
        
        .partner-name {
          font-size: 0.9rem;
          color: #374151;
        }
        
        @media (max-width: 768px) {
          .partner-info {
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 8px;
          }
          
          .partner-logo-container {
            width: 240px;
            height: 160px;
            flex: 0 1 240px;
          }
        }
      `}</style>
      <div className="course-list">
        <div className="course-header">
          <h1>Unsere Kurse</h1>
          <p className="course-count-highlight">Aktuell verfügbare Kurse: {activeCount}</p>
        </div>

        <div className="course-search-container">
          <div className="course-search-inner">
            <input
              type="text"
              className="course-search-input"
              placeholder="Kurse durchsuchen ..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Kurse durchsuchen"
            />
          </div>
          <div className="pager-edge pager-edge--left">
            {currentPage > 1 && (
              <button
                type="button"
                className="pager-btn"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                title="Vorherige Seite"
                aria-label="Vorherige Seite"
              >
                ‹
              </button>
            )}
          </div>
          <div className="pager-edge pager-edge--right">
            {currentPage < totalPages && (
              <button
                type="button"
                className="pager-btn"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                title="Nächste Seite"
                aria-label="Nächste Seite"
              >
                ›
              </button>
            )}
          </div>
        </div>

      {filteredCourses.length === 0 ? (
        <div className="empty-state">
          <h3>No courses available</h3>
          <p>Check back later for new courses or contact support if you believe this is an error.</p>
        </div>
      ) : (
        <>
          <div className="courses-grid">
            {paginatedCourses.map((course) => (
                <div 
                  key={course.id} 
                  className="course-card clickable" 
                  onClick={() => handleCourseClick(course)}
                  style={{ cursor: 'pointer' }}
                >
                {course.courseId && (
                  <span className="course-id-label">ID: {course.courseId}</span>
                )}
                {course.price && (
                  <span className="course-price-badge">€{course.price}</span>
                )}
                <div className="course-header">
                  <h3 className="course-title">{course.title || 'Untitled Course'}</h3>
                </div>
                
                <div className="course-info">

                  <div className="info-item">
                    <span className="label">Zeitraum:</span>
                    <span className="value">{(() => {
                      const start = course.startDate ? new Date(course.startDate).toLocaleDateString('de-DE') : null;
                      const end = course.endDate ? new Date(course.endDate).toLocaleDateString('de-DE') : null;
                      if (start && end) return `${start} – ${end}`;
                      return start || end || 'TBD';
                    })()}</span>
                  </div>

                  <div className="info-item">
                    <span className="label">Zielgruppe:</span>
                    <span className="value">{course.targetgroup || 'TBD'}</span>
                  </div>

{/* Debug partner data for each course */}
                  {(() => {
                    console.log(`🔍 Course "${course.title}" partner data:`, {
                      hasPartner: !!course.partner,
                      partnerId: course.partnerId,
                      partnerData: course.partner
                    });
                    return null;
                  })()}
                  
                  {course.partner ? (
                    <div className="info-item partner-item">
                      <span className="label">Kooperationspartner</span>
                      <div className="partner-info">
                        <div className="partner-logos">
                          {course.partner.logoPath && (
                            <div className="partner-logo-container">
                              <img 
                                src={partnerLogoUrls[course.partner.id] || (course.partner.logoPath.startsWith('/') || course.partner.logoPath.startsWith('http') ? course.partner.logoPath : `/assets/images/${course.partner.logoPath}`)}
                                alt={course.partner.name} 
                                className="partner-logo-small"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            </div>
                          )}
                          {/* Render secondary partner logo if we could augment it earlier */}
                          {course.partner2 && course.partner2.logoPath && (
                            <div className="partner-logo-container">
                              <img 
                                src={partnerLogoUrls[course.partner2.id] || (course.partner2.logoPath.startsWith('/') || course.partner2.logoPath.startsWith('http') ? course.partner2.logoPath : `/assets/images/${course.partner2.logoPath}`)}
                                alt={course.partner2.name} 
                                className="partner-logo-small"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="info-item partner-item">
                      <span className="label">Kooperationspartner</span>
                      <span className="value" style={{ color: '#999', fontStyle: 'italic' }}>
                        {course.partnerId ? `ID: ${course.partnerId} (No partner data)` : 'No partner assigned'}
                      </span>
                    </div>
                  )}

                  {/* Location and Price moved to header (under title and top-right badge) */}
                </div>
              </div>
            ))}
          </div>
          
        </>
      )}
      </div>
    </>
  );
};

export default CourseList;